import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import type { MtoMaterialPosition, MtoRun, ReconcileStatus } from '@entities/mto';
import {
  fmtNum,
  fmtQtyText,
  positionRaw,
  reconcileStatusLabel,
  reconcileStatusTone,
  runRaw,
} from '@entities/mto';
import { SourceVedomostFreshness } from './MtoVedomostFreshness';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { IconSearch } from '@shared/ui/icons';

const PAGE = 200;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const TileButton = styled.button<{ $active: boolean }>`
  text-align: left;
  font: inherit;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.backgroundElevated)};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const TileValue = styled.span`
  display: block;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TileLabel = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const SourceLine = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(3)};

  & > a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 420px;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(2.5)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.muted};
    pointer-events: none;
  }

  & > input {
    padding-left: ${({ theme }) => theme.spacing(8)};
  }
`;

const Counter = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TableScroll = styled.div`
  overflow: auto;
  max-height: 70vh;
  -webkit-overflow-scrolling: touch;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: none;
    box-shadow: inset 0 -1px 0 ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }

  th.num,
  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const Name = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

/** Наименование по РД — не уже 260 px: длинные названия иначе рвутся на
 *  пять-шесть строк. */
const NeedName = styled(Name)`
  min-width: 260px;
`;

const Small = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  overflow-wrap: anywhere;
`;

const Source = styled(Small)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const More = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Diff = styled.td<{ $tone: 'bad' | 'warn' | 'ok' }>`
  color: ${({ $tone, theme }) =>
    $tone === 'bad' ? theme.colors.status.danger : theme.colors.text.primary};
  font-weight: ${({ $tone }) => ($tone === 'ok' ? 400 : 700)};
`;

type Filter = ReconcileStatus | 'all';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'вся потребность' },
  { key: 'not_ordered', label: 'не заказано' },
  { key: 'under', label: 'заказано меньше' },
  { key: 'over', label: 'заказано больше' },
  { key: 'ok', label: 'заказано полностью' },
  { key: 'unit_mismatch', label: 'разные единицы' },
];

function statusOf(p: MtoMaterialPosition): ReconcileStatus | undefined {
  return positionRaw(p).reconcile?.status;
}

function matches(p: MtoMaterialPosition, q: string): boolean {
  if (!q) return true;
  const rec = positionRaw(p).reconcile;
  const hay = `${p.name} ${rec?.matched_name ?? ''}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

/**
 * Результат сверки потребности с заявками (модуль 1.4): плитки-фильтры по
 * итогу, таблица потребности с заявленным количеством и разницей, отдельно —
 * заявленное без потребности.
 */
export function MtoReconcileView({
  run,
  materials,
}: {
  run: MtoRun;
  materials: MtoMaterialPosition[];
}) {
  const raw = runRaw(run);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE);
  const withoutRef = useRef<HTMLDivElement>(null);
  const need = useMemo(() => materials.filter((p) => p.source_role === 'rd_table'), [materials]);
  const requests = useMemo(
    () => materials.filter((p) => p.source_role === 'request_table'),
    [materials],
  );
  const withoutNeed = requests.filter((p) => statusOf(p) === 'without_need');
  const count = (k: Filter) => (k === 'all' ? need.length : need.filter((p) => statusOf(p) === k).length);
  const rows = useMemo(
    () =>
      need.filter(
        (p) => (filter === 'all' || statusOf(p) === filter) && matches(p, search.trim()),
      ),
    [need, filter, search],
  );

  return (
    <>
      <Card>
        <Tiles>
          {FILTERS.filter((f) => f.key === 'all' || count(f.key) > 0).map((f) => (
            <TileButton
              key={f.key}
              type="button"
              $active={filter === f.key}
              aria-pressed={filter === f.key}
              onClick={() => {
                setFilter(f.key);
                setShown(PAGE);
              }}
            >
              <TileValue>{count(f.key)}</TileValue>
              <TileLabel>{f.label}</TileLabel>
            </TileButton>
          ))}
          {withoutNeed.length > 0 && (
            <TileButton
              type="button"
              $active={false}
              onClick={() => withoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              <TileValue>{withoutNeed.length}</TileValue>
              <TileLabel>заявлено без потребности — к списку</TileLabel>
            </TileButton>
          )}
        </Tiles>
        {raw.rd_run_id && (
          <SourceLine>
            Потребность из ведомости:{' '}
            <Link to={`/agents/mto/runs/${raw.rd_run_id}`}>
              {raw.rd_run_summary ?? 'открыть ведомость'}
            </Link>
          </SourceLine>
        )}
        {raw.rd_run_id && <SourceVedomostFreshness rdRunId={raw.rd_run_id} />}
      </Card>

      <Card>
        <SectionTitle>
          Потребность и заявки <Badge $tone="neutral">{rows.length}</Badge>
        </SectionTitle>
        <Filters>
          <SearchWrap>
            <IconSearch size={15} />
            <Input
              type="search"
              aria-label="Поиск по сверке"
              placeholder="Поиск: бетон, A500С…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShown(PAGE);
              }}
            />
          </SearchWrap>
          <Counter role="status">
            показано {Math.min(shown, rows.length)} из {rows.length}
          </Counter>
        </Filters>
        {rows.length === 0 ? (
          <Muted>
            {need.length === 0 ? 'Потребности нет — см. замечания.' : 'Под фильтр ничего не попало.'}
          </Muted>
        ) : (
          <TableScroll tabIndex={0} role="region" aria-label="Сверка потребности с заявками">
            <Table>
              <thead>
                <tr>
                  <th scope="col">Наименование по РД</th>
                  <th scope="col">Ед.</th>
                  <th scope="col" className="num">
                    Нужно
                  </th>
                  <th scope="col" className="num">
                    Заявлено
                  </th>
                  <th scope="col" className="num">
                    Разница
                  </th>
                  <th scope="col">Итог</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, shown).map((p) => {
                  const rec = positionRaw(p).reconcile;
                  const needQ = rec?.need ?? null;
                  const got = rec?.requested ?? null;
                  const status = rec?.status;
                  const rawDiff =
                    got != null && needQ != null
                      ? got - needQ
                      : status === 'not_ordered' && needQ != null
                        ? -needQ
                        : null;
                  // Хвост плавающей точки (1,042 − 1,042 = 2e-16) показывался
                  // «+0» / «-0» и красил полное совпадение как расхождение.
                  const diff = rawDiff == null ? null : Math.round(rawDiff * 1e6) / 1e6 || 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <NeedName>{p.name}</NeedName>
                        {rec?.matched_name && rec.matched_name !== p.name && (
                          <Small>
                            в заявке: «{rec.matched_name}»
                            {rec.fuzzy ? ' — сопоставлено по похожему наименованию, проверьте' : ''}
                          </Small>
                        )}
                        <Source title={p.source_ref}>{p.source_ref}</Source>
                      </td>
                      <td>{p.unit ?? '—'}</td>
                      {/* 4 знака: «0,0017 м3» в ведомости не должен стать «0,002» здесь. */}
                      <td className="num">{needQ != null ? fmtNum(needQ, 4) : fmtQtyText(p.quantity)}</td>
                      <td className="num">{fmtNum(got, 4)}</td>
                      {/* «Заказано полностью» — в допуске округления заявки
                          (72,98 на 72,9804): остаток разницы не красим. */}
                      <Diff
                        className="num"
                        $tone={
                          status === 'ok' || diff == null || diff === 0 ? 'ok' : diff < 0 ? 'bad' : 'warn'
                        }
                      >
                        {diff == null ? '—' : `${diff > 0 ? '+' : ''}${fmtNum(diff, 4)}`}
                      </Diff>
                      <td>
                        <Badge $tone={reconcileStatusTone(status)}>{reconcileStatusLabel(status)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {rows.length > shown && (
          <More>
            <Button type="button" $variant="secondary" $size="sm" onClick={() => setShown((n) => n + PAGE)}>
              Показать ещё {Math.min(PAGE, rows.length - shown)}
            </Button>
          </More>
        )}
      </Card>

      {withoutNeed.length > 0 && (
        <Card ref={withoutRef}>
          <SectionTitle>
            Заявлено без потребности по РД <Badge $tone="warning">{withoutNeed.length}</Badge>
          </SectionTitle>
          <TableScroll tabIndex={0} role="region" aria-label="Заявлено без потребности">
            <Table>
              <thead>
                <tr>
                  <th scope="col">Позиция заявки</th>
                  <th scope="col">Ед.</th>
                  <th scope="col" className="num">
                    Кол-во
                  </th>
                  <th scope="col">Источник</th>
                </tr>
              </thead>
              <tbody>
                {withoutNeed.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Name>{p.name}</Name>
                    </td>
                    <td>{p.unit ?? '—'}</td>
                    <td className="num">{fmtQtyText(p.quantity)}</td>
                    <td>
                      <Source title={p.source_ref}>{p.source_ref}</Source>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Card>
      )}
    </>
  );
}
